import {
  Alerte,
  Badge,
  Bouton,
  Carte,
  CelluleTableau,
  ConteneurPage,
  EnTetePage as EnTetePageUi,
  EtatTableauChargement,
  EtatTableauVide,
  GroupeActions,
  GroupeChamp,
  GroupeHorizontal,
  Tableau
} from "@alpha/ui";
import type { FormHTMLAttributes, HTMLAttributes, ReactNode } from "react";

function joindreClasses(...classes: Array<string | false | null | undefined>): string | undefined {
  const resultat = classes.filter(Boolean).join(" ");
  return resultat.length > 0 ? resultat : undefined;
}

export function CoqueApplication({
  navigation,
  barreSuperieure,
  children
}: {
  navigation: ReactNode;
  barreSuperieure?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="alpha-coque-application">
      <NavigationLaterale>{navigation}</NavigationLaterale>
      <div className="alpha-zone-contenu">
        {barreSuperieure ? <BarreSuperieure>{barreSuperieure}</BarreSuperieure> : null}
        <ConteneurPage>{children}</ConteneurPage>
      </div>
    </div>
  );
}

export function NavigationLaterale({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return <aside className={joindreClasses("alpha-navigation-laterale", className)} {...props} />;
}

export function BarreSuperieure({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-barre-superieure", className)} {...props} />;
}

export function BarreFiltres({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-barre-filtres", className)} {...props} />;
}

export interface ColonneTableauDonnees<Element> {
  id: string;
  titre: ReactNode;
  rendu: (element: Element) => ReactNode;
  largeur?: string;
}

export interface TableauDonneesProps<Element> {
  colonnes: Array<ColonneTableauDonnees<Element>>;
  elements: Element[];
  obtenirCle: (element: Element) => string;
  chargement?: boolean;
  erreur?: ReactNode;
  vide?: ReactNode;
  actions?: (element: Element) => ReactNode;
}

export function TableauDonnees<Element>({
  actions,
  chargement = false,
  colonnes,
  elements,
  erreur,
  obtenirCle,
  vide = "Aucune donnée ne correspond aux critères."
}: TableauDonneesProps<Element>) {
  if (erreur) {
    return <Alerte tonalite="danger">{erreur}</Alerte>;
  }

  return (
    <div className="alpha-tableau-donnees">
      <Tableau>
        <thead>
          <tr>
            {colonnes.map((colonne) => (
              <th key={colonne.id} style={{ width: colonne.largeur }}>
                {colonne.titre}
              </th>
            ))}
            {actions ? <th>Actions</th> : null}
          </tr>
        </thead>
        {chargement ? (
          <EtatTableauChargement colSpan={colonnes.length + (actions ? 1 : 0)} />
        ) : elements.length === 0 ? (
          <EtatTableauVide colSpan={colonnes.length + (actions ? 1 : 0)}>{vide}</EtatTableauVide>
        ) : (
          <tbody>
            {elements.map((element) => (
              <tr key={obtenirCle(element)}>
                {colonnes.map((colonne) => (
                  <CelluleTableau key={colonne.id}>{colonne.rendu(element)}</CelluleTableau>
                ))}
                {actions ? <CelluleTableau>{actions(element)}</CelluleTableau> : null}
              </tr>
            ))}
          </tbody>
        )}
      </Tableau>
    </div>
  );
}

export function PaginationServeur({
  page,
  totalPages,
  surChangement
}: {
  page: number;
  totalPages: number;
  surChangement: (page: number) => void;
}) {
  return (
    <GroupeHorizontal>
      <Bouton disabled={page <= 1} onClick={() => surChangement(page - 1)} variante="secondaire">
        Page précédente
      </Bouton>
      <span>
        Page {page} sur {totalPages}
      </span>
      <Bouton
        disabled={page >= totalPages}
        onClick={() => surChangement(page + 1)}
        variante="secondaire"
      >
        Page suivante
      </Bouton>
    </GroupeHorizontal>
  );
}

export function FormulaireStandard({ className, ...props }: FormHTMLAttributes<HTMLFormElement>) {
  return <form className={joindreClasses("alpha-formulaire-standard", className)} {...props} />;
}

export function FormulaireEnSections({
  sections
}: {
  sections: Array<{ titre: ReactNode; contenu: ReactNode }>;
}) {
  return (
    <div className="alpha-formulaire-sections">
      {sections.map((section, index) => (
        <Carte key={index}>
          <h2>{section.titre}</h2>
          {section.contenu}
        </Carte>
      ))}
    </div>
  );
}

export function ConfirmationSuppression({
  libelle = "Supprimer",
  surConfirmation
}: {
  libelle?: string;
  surConfirmation: () => void;
}) {
  return (
    <GroupeActions>
      <Bouton onClick={surConfirmation} variante="danger">
        {libelle}
      </Bouton>
    </GroupeActions>
  );
}

export function PanneauDetails({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <aside
      className={joindreClasses("alpha-panneau alpha-panneau-details", className)}
      {...props}
    />
  );
}

export function MiseEnPageListeDetails({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-mise-page-liste-details", className)} {...props} />;
}

export function CarteIndicateur({
  libelle,
  valeur,
  tonalite = "neutre"
}: {
  libelle: ReactNode;
  valeur: ReactNode;
  tonalite?: "neutre" | "succes" | "danger" | "information" | "avertissement";
}) {
  return (
    <Carte className="alpha-carte-indicateur">
      <Badge tonalite={tonalite}>{libelle}</Badge>
      <strong>{valeur}</strong>
    </Carte>
  );
}

export function GrilleIndicateurs({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-grille-responsive", className)} {...props} />;
}

export function PageListe({
  titre,
  actions,
  filtres,
  children
}: {
  titre: ReactNode;
  actions?: ReactNode;
  filtres?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <EnTetePageUi>
        <div>
          <h1>{titre}</h1>
        </div>
        {actions}
      </EnTetePageUi>
      {filtres ? <BarreFiltres>{filtres}</BarreFiltres> : null}
      {children}
    </>
  );
}

export function PageFormulaire({ titre, children }: { titre: ReactNode; children: ReactNode }) {
  return (
    <>
      <EnTetePageUi>
        <h1>{titre}</h1>
      </EnTetePageUi>
      {children}
    </>
  );
}

export function PageErreur({ titre, message }: { titre: ReactNode; message: ReactNode }) {
  return (
    <Alerte tonalite="danger">
      <strong>{titre}</strong>
      <p>{message}</p>
    </Alerte>
  );
}

export function PageAccesRefuse() {
  return <PageErreur titre="Accès refusé" message="Vous ne pouvez pas accéder à cette page." />;
}

export function SelecteurEntite<Element>({
  elements,
  obtenirCle,
  obtenirLibelle,
  valeur,
  surChangement
}: {
  elements: Element[];
  obtenirCle: (element: Element) => string;
  obtenirLibelle: (element: Element) => string;
  valeur?: string;
  surChangement: (valeur: string) => void;
}) {
  return (
    <GroupeChamp>
      <select
        className="alpha-ui alpha-liste-deroulante"
        onChange={(evenement) => surChangement(evenement.currentTarget.value)}
        value={valeur}
      >
        {elements.map((element) => (
          <option key={obtenirCle(element)} value={obtenirCle(element)}>
            {obtenirLibelle(element)}
          </option>
        ))}
      </select>
    </GroupeChamp>
  );
}

export function ZoneTeleversement({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-ui alpha-carte", className)} {...props} />;
}
